import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { getAccessToken } from '../store/actions/auth';
import API_BASE_URL from '../config/baseUrl';
import { formatDisplayUserId } from '../utils/userIdFormat';

// Fetch agent plus minus data
const fetchAgentPlusMinus = async function(matchId, userId) {
  try {
    const token = getAccessToken();
    
    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/ant-pro/get-agent-plus-minus",
      {
        matchId: matchId,
        userId: userId
      },
      { headers: { Authorization: "Bearer " + token } }
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to fetch data" };
    }

    return { success: true, data: data.data };
  } catch (err) {
    const errorMessage = err.response?.data?.message || "Failed to fetch data";
    return { success: false, error: errorMessage };
  }
};

const AgentPlusMinusPage = () => {
  const navigate = useNavigate();
  const { matchId, matchName } = useParams();
  const { user } = useSelector(function(state) {
    return state.auth;
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agentData, setAgentData] = useState([]);
  const [currentParentId, setCurrentParentId] = useState(user?.userId || '');

  const getRoleLabelFromUserId = function(userId) {
    if (!userId || typeof userId !== 'string') return '';
    if (userId.startsWith('SAD')) return 'Super Admin';
    if (userId.startsWith('AD')) return 'Admin';
    if (userId.startsWith('SUB')) return 'Mini Admin';
    if (userId.startsWith('M')) return 'Master';
    if (userId.startsWith('SA')) return 'Super Agent';
    if (userId.startsWith('A')) return 'Agent';
    if (userId.startsWith('C')) return 'Client';
    return '';
  };

  const getNextLowerRoleLabel = function(userId) {
    const role = getRoleLabelFromUserId(userId);
    const order = ['Super Admin', 'Admin', 'Mini Admin', 'Master', 'Super Agent', 'Agent', 'Client'];
    const index = order.indexOf(role);
    if (index === -1 || index >= order.length - 1) return '';
    return order[index + 1];
  };

  useEffect(function() {
    const loadData = async function() {
      setLoading(true);
      setError(null);

      const result = await fetchAgentPlusMinus(matchId, currentParentId || "");

      if (result.success) {
        setAgentData(result.data || []);
      } else {
        setError(result.error);
      }

      setLoading(false);
    };

    if (matchId) {
      loadData();
    }
  }, [matchId, currentParentId]);

  // Calculate total
  const totalPnl = agentData.reduce(function(sum, item) {
    return sum + (item.pnl || 0);
  }, 0);
  const currentListLevelLabel = agentData.length > 0
    ? getRoleLabelFromUserId(agentData[0].userId)
    : getNextLowerRoleLabel(currentParentId || user?.userId || '');

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-gray-600 px-5 pt-2 flex items-center justify-between">
              <div>
                <span className="text-2xl text-white font-normal capitalize">
                  Client Report
                </span>
                <br />
                <p className="text-lg text-white pb-2">
                  {decodeURIComponent(matchName || '')}
                </p>
              </div>
              <button 
                onClick={function() { navigate(-1); }}
                className="bg-blue-500 text-white px-4 py-2 hover:bg-blue-600"
              >
                Back
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error}</div>
              ) : (
                <table className="w-full border-collapse text-sm" style={{ tableLayout: 'auto' }}>
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 p-2 text-left font-medium">
                        {currentListLevelLabel || 'User'}
                      </th>
                      <th className="border border-gray-300 p-2 text-left font-medium"></th>
                      <th className="border border-gray-300 p-2 text-left font-medium">Net Account</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentData.length === 0 ? (
                      <tr>
                        <td colSpan="3" className="border border-gray-300 p-8">
                          <div className="flex flex-col items-center justify-center text-gray-400">
                            <svg className="w-16 h-16 mb-4" viewBox="0 0 64 41" xmlns="http://www.w3.org/2000/svg">
                              <g transform="translate(0 1)" fill="none" fillRule="evenodd">
                                <ellipse className="fill-gray-200" cx="32" cy="33" rx="32" ry="7"></ellipse>
                                <g className="fill-gray-300" fillRule="nonzero">
                                  <path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z"></path>
                                  <path d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"></path>
                                </g>
                              </g>
                            </svg>
                            <div className="text-gray-500">No data</div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      agentData.map(function(item, index) {
                        const nextRoleLabel = getNextLowerRoleLabel(item.userId);
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="border border-gray-300 p-2">
                              {formatDisplayUserId(item.userId)} ({item.username})
                            </td>
                            <td className="border border-gray-300 p-2">
                              {nextRoleLabel ? (
                                <button
                                  onClick={function() { setCurrentParentId(item.userId); }}
                                  className="bg-blue-500 text-white px-3 py-1 hover:bg-blue-600"
                                  style={{ borderRadius: 0 }}
                                >
                                  {nextRoleLabel}
                                </button>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td className="border border-gray-300 p-2">
                              <span className={item.pnl >= 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                {item.pnl.toFixed(2)}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                  {agentData.length > 0 && (
                    <tfoot className="bg-gray-200">
                      <tr>
                        <td className="border border-gray-300 p-2 font-bold" colSpan="2">
                          Total
                        </td>
                        <td className="border border-gray-300 p-2">
                          <span className={totalPnl >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                            {totalPnl.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentPlusMinusPage;
